import React, { useEffect, useState, useMemo, useRef } from 'react';
import { buttonClass, IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { IconChevronLeft, IconChevronRight } from '../components/ui/icons';
import CrewScheduleTimeline from './CrewScheduleTimeline';
import useUserStore from '../stores/useUserStore';
import useMyShowsPrefsStore from '../stores/useMyShowsPrefsStore';
import { apiFetchAuthed } from '../apiHelpers';
import { OOOEntry, UnavailabilityBlock } from './types';
import { UNAVAILABLE_STYLE } from './schedule-colors';
import { parseDate, formatEventDate, shiftDay } from '../utils/date-utils';
import { todayYmd } from './day-utils';
import PageLayout from '../components/ui/PageLayout';
import Tag from '../components/ui/Tag';
import Dialog from '../components/ui/Dialog';
import LogoSpinner from '../components/ui/LogoSpinner';
import { ListRow, ListStack } from '../components/ui/ListRow';
import FadeIn from '../components/ui/FadeIn';
import Appear from '../components/ui/Appear';
import Checkbox from '../components/ui/Checkbox';
import { Field, FieldRow, TextArea } from '../components/ui/Field';

interface MyShow {
  id: string;
  name: string;
  event_date: string;
  end_date: string | null;
  location: string;
  status: string;
  show_time: string | null;
  role: string;
}

// A rolling window, not a calendar month: crew read this page as "what is
// coming", and a show on the 2nd of next month is coming on the 28th of this one.
const WINDOW_DAYS = 30;

const MySchedulePage: React.FC = () => {
  const user = useUserStore(s => s.user);
  const hasTravelTier = !!user.travel_tier;
  const hidePending = useMyShowsPrefsStore(s => s.hidePending);
  const setHidePending = useMyShowsPrefsStore(s => s.setHidePending);

  const today = todayYmd();
  const [windowStart, setWindowStart] = useState(today);
  const windowEnd = shiftDay(windowStart, WINDOW_DAYS - 1);
  const [shows, setShows] = useState<MyShow[]>([]);
  const [oooEntries, setOOOEntries] = useState<OOOEntry[]>([]);
  const [unavailabilityBlocks, setUnavailabilityBlocks] = useState<UnavailabilityBlock[]>([]);
  const [counts, setCounts] = useState<{ window: number; trailing: number }>({ window: 0, trailing: 0 });
  const [loading, setLoading] = useState(true);

  // OOO form
  const [showOOOForm, setShowOOOForm] = useState(false);
  const [editingOOO, setEditingOOO] = useState<OOOEntry | null>(null);
  const [oooForm, setOOOForm] = useState({
    start_date: '', end_date: '', is_tentative: false, entered_in_onpay: false, notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  // What the OOO form opened with, so typing a value and deleting it reads as clean.
  const oooOpened = useRef('');
  // Toggling or paging twice inside one round trip: only the latest request
  // may write state, or the earlier reply lands last and contradicts the box.
  const fetchSeq = useRef(0);

  const fetchData = async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const [showsData, oooData, blocksData] = await Promise.all([
        apiFetchAuthed(`/schedule/my-shows?from=${windowStart}&to=${windowEnd}${hidePending ? '&hide_pending=true' : ''}`),
        apiFetchAuthed('/schedule/my-ooo'),
        apiFetchAuthed('/schedule/my-unavailability'),
      ]);
      if (seq !== fetchSeq.current) return;
      setShows(showsData.shows || []);
      setCounts(showsData.counts || { window: 0, trailing: 0 });
      setOOOEntries(oooData || []);
      setUnavailabilityBlocks(blocksData || []);
    } catch (err) {
      console.error('Error fetching schedule:', err);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (hasTravelTier) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStart, hasTravelTier, hidePending]);

  const navigate = (delta: number) => setWindowStart(shiftDay(windowStart, delta * WINDOW_DAYS));

  const handleOOOSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingOOO) {
        await apiFetchAuthed(`/schedule/my-ooo/${editingOOO.id}`, {
          method: 'PATCH',
          body: oooForm,
        });
      } else {
        await apiFetchAuthed('/schedule/my-ooo', {
          method: 'POST',
          body: oooForm,
        });
      }
      setShowOOOForm(false);
      setEditingOOO(null);
      fetchData();
    } catch (err) {
      console.error('Error saving OOO:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOOO = async (id: string) => {
    if (!window.confirm('Delete this OOO entry?')) return;
    try {
      await apiFetchAuthed(`/schedule/my-ooo/${id}`, { method: 'DELETE' });
      fetchData();
    } catch (err) {
      console.error('Error deleting OOO:', err);
    }
  };

  const startEditOOO = (entry: OOOEntry) => {
    setEditingOOO(entry);
    const form = {
      start_date: entry.start_date.slice(0, 10),
      end_date: entry.end_date.slice(0, 10),
      is_tentative: entry.is_tentative,
      entered_in_onpay: entry.entered_in_onpay,
      notes: entry.notes || '',
    };
    oooOpened.current = JSON.stringify(form);
    setOOOForm(form);
    setShowOOOForm(true);
  };

  const startNewOOO = () => {
    setEditingOOO(null);
    const form = { start_date: '', end_date: '', is_tentative: false, entered_in_onpay: false, notes: '' };
    oooOpened.current = JSON.stringify(form);
    setOOOForm(form);
    setShowOOOForm(true);
  };

  // OOO entries and blocks overlapping the window
  const windowOOO = useMemo(() =>
    oooEntries.filter(o => o.start_date.slice(0, 10) <= windowEnd && o.end_date.slice(0, 10) >= windowStart),
    [oooEntries, windowStart, windowEnd]
  );
  const windowBlocks = useMemo(() =>
    unavailabilityBlocks.filter(
      b => b.start_date.slice(0, 10) <= windowEnd && b.end_date.slice(0, 10) >= windowStart,
    ),
    [unavailabilityBlocks, windowStart, windowEnd]
  );

  if (!hasTravelTier) {
    return (
      <div className="min-h-page flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <h2 className="text-xl font-semibold text-on-surface mb-3">My Schedule</h2>
          <p className="text-on-surface-variant">
            Your account isn't set up for travel scheduling yet.
            Contact a producer or admin to get added to the travel crew.
          </p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Tooltip label="Previous window">
          <IconButton label="Previous window" onClick={() => navigate(-1)}><IconChevronLeft /></IconButton>
          </Tooltip>
          <h2 className="text-xl font-semibold text-on-surface">
            {formatEventDate(windowStart, 'MMM D')} – {formatEventDate(windowEnd, 'MMM D, YYYY')}
          </h2>
          <Tooltip label="Next window">
          <IconButton label="Next window" onClick={() => navigate(1)}><IconChevronRight /></IconButton>
          </Tooltip>
          <Appear show={windowStart !== today}>
            <button
              onClick={() => setWindowStart(today)}
              className={buttonClass('tonal', 'sm')}
            >
              Today
            </button>
          </Appear>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-on-surface-variant">
            <span className="text-on-surface font-medium">{counts.window}</span> in these {WINDOW_DAYS} days
            <span className="mx-2">·</span>
            <span className="text-on-surface font-medium">{counts.trailing}</span> in the 90 before
          </div>
          <button
            onClick={startNewOOO}
            className={buttonClass('tonal', 'md')}
          >
            + OOO
          </button>
        </div>
      </div>

      {loading && <LogoSpinner label="Loading your schedule" />}

      {!loading && (
        <FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Shows column */}
          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider">
                My Shows ({shows.length})
              </h3>
              {/* Also drives the dashboard's My Upcoming Shows module, which has
                  no control of its own. */}
              <Checkbox
                size="sm"
                label="Hide pending"
                checked={hidePending}
                onChange={e => setHidePending(e.target.checked)}
              />
            </div>
            <ListStack
              items={shows}
              getKey={show => String(show.id)}
              spacing="loose"
              label="My shows"
              empty={hidePending ? 'No confirmed shows in these 30 days.' : 'No shows in these 30 days.'}
              renderRow={show => {
                const daysUntil = parseDate(show.event_date).diff(parseDate(new Date().toISOString().slice(0, 10)), 'day');
                return (
                  <ListRow
                    to={`/events/${show.id}`}
                    title={show.name}
                    meta={`${formatEventDate(show.event_date)}${show.location ? ` · ${show.location}` : ''}${show.show_time ? ` · ${show.show_time}` : ''}`}
                    trailing={(
                      <>
                        {show.status === 'pending' && (
                          <Tag>Pending</Tag>
                        )}
                        {show.role && (
                          <Tag tone="accent">{show.role}</Tag>
                        )}
                        <Tag tone={daysUntil <= 3 ? 'bad' : daysUntil <= 7 ? 'warn' : 'info'}>
                          {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil}d`}
                        </Tag>
                      </>
                    )}
                  />
                );
              }}
            />
          </div>

          {/* OOO column */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider">
              Out of Office ({windowOOO.length})
            </h3>
            <ListStack
              items={windowOOO}
              getKey={o => String(o.id)}
              spacing="loose"
              label="Out of office"
              empty="No OOO entries in these 30 days."
              renderRow={o => (
                <ListRow>
                  <div className="min-w-0">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm text-on-surface font-medium">
                        {formatEventDate(o.start_date.slice(0, 10))}
                        {o.start_date.slice(0, 10) !== o.end_date.slice(0, 10)
                          ? ` – ${formatEventDate(o.end_date.slice(0, 10))}`
                          : ''}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {o.is_tentative && (
                          <Tag size="sm">Tentative</Tag>
                        )}
                        {o.entered_in_onpay && (
                          <Tag size="sm" tone="good">OnPay</Tag>
                        )}
                      </div>
                    </div>
                    {o.notes && <p className="text-xs text-on-surface-variant mb-2">{o.notes}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEditOOO(o)}
                        className={buttonClass('quiet', 'sm')}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteOOO(o.id)}
                        className={buttonClass('danger-quiet', 'sm')}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </ListRow>
              )}
            />

            {/* Scheduler-authored unavailability blocks (read-only).
                Shown so the user understands when/why they weren't booked.
                Distinct from OOO above — these are placed BY a scheduler,
                not self-reported. Notes show verbatim. */}
            {windowBlocks.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider pt-4">
                  Marked Unavailable ({windowBlocks.length})
                </h3>
                <div className="text-[10px] text-on-surface-variant mb-1">
                  Set by a scheduler — read-only. Contact a producer to discuss.
                </div>
                {windowBlocks.map(b => (
                  <div
                    key={b.id}
                    className={`rounded-lg px-4 py-3 border ${UNAVAILABLE_STYLE.bg} ${UNAVAILABLE_STYLE.text} ${UNAVAILABLE_STYLE.border} ${UNAVAILABLE_STYLE.hatch}`}
                  >
                    <div className="text-sm font-medium">
                      {formatEventDate(b.start_date.slice(0, 10))}
                      {b.start_date.slice(0, 10) !== b.end_date.slice(0, 10)
                        ? ` – ${formatEventDate(b.end_date.slice(0, 10))}`
                        : ''}
                    </div>
                    {b.notes && <p className="text-xs mt-1 opacity-80">{b.notes}</p>}
                  </div>
                ))}
              </>
            )}

            {/* All OOO (not just this window) */}
            {oooEntries.length > windowOOO.length && (
              <div className="pt-3 border-t border-gray-700/40">
                <h4 className="text-[10px] text-on-surface-variant uppercase tracking-wider mb-2">
                  All OOO Entries ({oooEntries.length})
                </h4>
                {oooEntries.filter(o => !windowOOO.includes(o)).slice(0, 5).map(o => (
                  <div key={o.id} className="text-xs text-on-surface-variant py-1 flex justify-between">
                    <span>
                      {formatEventDate(o.start_date.slice(0, 10))}
                      {o.start_date.slice(0, 10) !== o.end_date.slice(0, 10)
                        ? ` – ${formatEventDate(o.end_date.slice(0, 10))}`
                        : ''}
                    </span>
                    <button onClick={() => startEditOOO(o)} className={buttonClass('quiet', 'sm')}>Edit</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </FadeIn>
      )}

      {/* Inline OOO form */}
      <Dialog
        open={showOOOForm}
        onClose={() => setShowOOOForm(false)}
        title={editingOOO ? 'Edit OOO' : 'Add Out of Office'}
        size="md"
        busy={submitting}
        dirty={showOOOForm && JSON.stringify(oooForm) !== oooOpened.current}
        footer={(
          <>
            <button type="button" onClick={() => { setShowOOOForm(false); setEditingOOO(null); }}
              className={buttonClass('quiet', 'md')}>
              Cancel
            </button>
            <button type="submit" form="my-ooo-form" disabled={submitting}
              className={buttonClass('filled', 'md')}>
              {submitting ? 'Saving...' : editingOOO ? 'Update' : 'Add OOO'}
            </button>
          </>
        )}
      >
        <form id="my-ooo-form" onSubmit={handleOOOSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Start Date">
              <Field type="date" value={oooForm.start_date}
                onChange={e => setOOOForm(f => ({ ...f, start_date: e.target.value }))}
                required />
            </FieldRow>
            <FieldRow label="End Date">
              <Field type="date" value={oooForm.end_date}
                onChange={e => setOOOForm(f => ({ ...f, end_date: e.target.value }))}
                min={oooForm.start_date} required />
            </FieldRow>
          </div>
          <div className="flex items-center gap-6">
            <Checkbox label="Tentative" checked={oooForm.is_tentative}
              onChange={e => setOOOForm(f => ({ ...f, is_tentative: e.target.checked }))} />
            <Checkbox label="Entered in OnPay" checked={oooForm.entered_in_onpay}
              onChange={e => setOOOForm(f => ({ ...f, entered_in_onpay: e.target.checked }))} />
          </div>
          <FieldRow label="Notes">
            <TextArea value={oooForm.notes}
              onChange={e => setOOOForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Reason, etc." />
          </FieldRow>
        </form>
      </Dialog>

      {/* The full staff timeline, read only (ff-feedback items 7 + 14):
          A 134-day grid takes the page width; the personal list above it
          does not need it. */}
      <div className="mt-2">
        <CrewScheduleTimeline />
      </div>
    </PageLayout>
  );
};

export default MySchedulePage;
